// src/views/Admin/Locales/LocalPage/components/PaymentHistoryFolder/PaymentHistoryFolder.tsx

import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { HiOutlineChevronDown, HiOutlineChevronRight, HiOutlineCurrencyDollar, HiTrash } from 'react-icons/hi2';
import { toast } from 'react-toastify';
import { getLocalPlanPaymentByLocalId, deleteLocalPlanPayment } from '../../../../../../crud/configuration_api/local_plan_payments';
import MiniLoadingSpinner from '../../../../../../components/Spinners/MiniLoadingSpinner';
import './PaymentHistoryFolder.scss';

interface Payment {
  local_plan_payment_id: number;  // Corregido para coincidir con el backend
  payment_id?: number;  // Alias opcional para compatibilidad
  local_id: number;
  payment_amount: number;
  payment_date: string;
  payment_method: string | null;
  notes: string | null;
  days_since_last_payment: number | null;
  created_by?: string;  // Campo directo del servidor
  created_by_user_id?: number;  // Campo del servidor
  local_plans_payments_metadata?: {
    created_by?: string;
    registered_at?: string;
    [key: string]: any;
  };
}

interface PaymentHistoryFolderProps {
  localId: number;
  localName: string;
}

export interface PaymentHistoryFolderRef {
  refreshPayments: () => void;
}

const PaymentHistoryFolder = forwardRef<PaymentHistoryFolderRef, PaymentHistoryFolderProps>(({ localId, localName }, ref) => {
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [deletingPaymentId, setDeletingPaymentId] = useState<number | null>(null);

  useEffect(() => {
    if (!collapsed) {
      fetchPayments();
    }
  }, [collapsed, localId]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const response = await getLocalPlanPaymentByLocalId(localId);
      if (response.status === 200) {
        const sortedPayments = response.data.sort((a: Payment, b: Payment) => 
          new Date(b.payment_date).getTime() - new Date(a.payment_date).getTime()
        );
        setPayments(sortedPayments);
      }
    } catch (err: any) {
      console.error('Error fetching payments:', err);
      if (err?.response?.status !== 404) {
        toast.error('Error al cargar el historial de pagos');
      } else {
        setPayments([]);
      }
    }
    setLoading(false);
  };

  // Expose refresh function to parent component
  useImperativeHandle(ref, () => ({
    refreshPayments: fetchPayments
  }));

  const handleDeletePayment = async (paymentId: number) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este pago?')) {
      return;
    }

    setDeletingPaymentId(paymentId);
    try {
      const response = await deleteLocalPlanPayment(paymentId);
      if ((response as any).status === 200) {
        toast.success('Pago eliminado exitosamente');
        setPayments(prev => prev.filter(p => p.local_plan_payment_id !== paymentId));
      } else {
        toast.error('Error al eliminar el pago');
      }
    } catch (err: any) {
      console.error('Error deleting payment:', err);
      const errorMsg = err?.response?.data?.message || 'Error al eliminar el pago';
      toast.error(errorMsg);
    }
    setDeletingPaymentId(null);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateTotal = () => {
    return payments.reduce((sum, payment) => sum + payment.payment_amount, 0);
  };

  return (
    <div className="payment-history-folder-card">
      <div className="payment-history-folder-header" onClick={() => setCollapsed(!collapsed)}>
        <div className="payment-folder-title">
          <button className="payment-folder-collapse-toggle">
            {collapsed ? <HiOutlineChevronRight size={20} /> : <HiOutlineChevronDown size={20} />}
          </button>
          <HiOutlineCurrencyDollar size={20} className="payment-folder-icon" />
          <h4>Historial de Pagos</h4>
          {!collapsed && payments.length > 0 && (
            <span className="payment-folder-count">{payments.length} pagos</span>
          )}
        </div>
      </div>

      {!collapsed && (
        <div className="payment-history-folder-body">
          {loading ? (
            <div className="payment-folder-loading">
              <MiniLoadingSpinner />
              <p>Cargando historial de pagos...</p>
            </div>
          ) : payments.length === 0 ? (
            <div className="payment-folder-no-payments">
              <p>No hay pagos registrados para este local.</p>
            </div>
          ) : (
            <>
              <div className="payment-folder-table-container">
                <table className="payment-folder-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Monto</th>
                      <th>Método</th>
                      <th>Días desde último</th>
                      <th>Creado por</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment) => (
                      <tr key={payment.local_plan_payment_id}>
                        <td>{formatDate(payment.payment_date)}</td>
                        <td className="payment-folder-amount-cell">{formatCurrency(payment.payment_amount)}</td>
                        <td>{payment.local_plans_payments_metadata?.payment_method || payment.payment_method || '-'}</td>
                        <td className="payment-folder-days-cell">
                          {payment.days_since_last_payment !== null 
                            ? `${payment.days_since_last_payment} días` 
                            : 'Primer pago'}
                        </td>
                        <td>
                          {payment.local_plans_payments_metadata?.created_by || 
                           payment.created_by || '-'}
                        </td>
                        <td>
                          <button
                            className="payment-folder-delete-button"
                            onClick={() => handleDeletePayment(payment.local_plan_payment_id)}
                            disabled={deletingPaymentId === payment.local_plan_payment_id}
                            title="Eliminar pago"
                          >
                            {deletingPaymentId === payment.local_plan_payment_id ? (
                              <MiniLoadingSpinner />
                            ) : (
                              <HiTrash size={18} />
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="payment-folder-summary">
                <div className="payment-folder-summary-item">
                  <span className="payment-folder-summary-label">Total de Pagos:</span>
                  <span className="payment-folder-summary-value">{payments.length}</span>
                </div>
                <div className="payment-folder-summary-item">
                  <span className="payment-folder-summary-label">Monto Total:</span>
                  <span className="payment-folder-summary-value payment-folder-total-amount">{formatCurrency(calculateTotal())}</span>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
});

PaymentHistoryFolder.displayName = 'PaymentHistoryFolder';

export default PaymentHistoryFolder;
